import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Star, MapPin, Heart, Clock, ArrowRight, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { ProviderBadge } from '../ProviderBadge';

// Deterministic aspect ratio picker. Same provider_id always yields the
// same shape, so the masonry layout never reshuffles between renders or
// page revisits. Mix of portrait, square, and landscape gives the
// Pinterest pin-wall feel rather than a uniform card grid.
const COVER_ASPECTS = [
  'aspect-[3/4]',  // tall portrait
  'aspect-[4/5]',  // medium portrait
  'aspect-square', // square
  'aspect-[4/3]',  // landscape
  'aspect-[3/2]',  // wide landscape
];
function pickCoverAspect(providerId) {
  if (!providerId) return 'aspect-[3/2]';
  let sum = 0;
  for (let i = 0; i < providerId.length; i += 1) sum += providerId.charCodeAt(i);
  return COVER_ASPECTS[sum % COVER_ASPECTS.length];
}

function formatDuration(mins) {
  if (!mins) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

function StarRating({ rating }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.3;
  return (
    <span className="inline-flex items-center gap-px">
      {[...Array(5)].map((_, i) => (
        <Star
          key={`star-${i}`}
          className={`h-3.5 w-3.5 ${
            i < full
              ? 'text-amber-400 fill-amber-400'
              : i === full && half
              ? 'text-amber-400 fill-amber-400/50'
              : 'text-stone-200 fill-stone-200'
          }`}
        />
      ))}
    </span>
  );
}

function ServiceRow({ svc }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-[#1A1A1A] font-medium line-clamp-1">{svc.name}</p>
        {svc.duration_minutes && (
          <p className="text-xs text-stone-400 flex items-center gap-1 mt-0.5">
            <Clock className="h-3 w-3" />
            {formatDuration(svc.duration_minutes)}
          </p>
        )}
      </div>
      <span className="text-sm font-semibold text-[#1A1A1A] whitespace-nowrap">&pound;{svc.price}</span>
    </div>
  );
}

export function ProviderCard({ provider, isFavourited, onToggleFavourite, isLoggedIn }) {
  const navigate = useNavigate();

  const handleFavClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      toast.info('Sign in to save favourites');
      navigate('/login');
      return;
    }
    onToggleFavourite(provider.provider_id);
  };

  const topServices = provider.top_services || [];
  const totalServices = provider.service_count || 0;
  const hasMoreServices = totalServices > 3;

  return (
    <Link
      to={`/@${provider.username}`}
      className="card-surface overflow-hidden card-hover group flex-shrink-0 w-full sm:w-auto"
      data-testid={`browse-provider-${provider.provider_id}`}
    >
      {/* Cover Image */}
      <div className={`${pickCoverAspect(provider.provider_id)} bg-[#F9F6F0] relative overflow-hidden`}>
        {provider.profile_image ? (
          <img src={provider.profile_image} alt={provider.brand_name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="font-heading text-5xl text-[#D4AF37]">{provider.brand_name?.charAt(0)}</span>
          </div>
        )}
        <button
          onClick={handleFavClick}
          className={`absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
            isFavourited
              ? 'bg-white/90 backdrop-blur-sm text-red-500'
              : 'bg-black/20 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100'
          } ${isFavourited ? 'opacity-100' : ''}`}
          data-testid={`fav-heart-${provider.provider_id}`}
        >
          <Heart className={`h-4 w-4 ${isFavourited ? 'fill-current' : ''}`} />
        </button>
        {provider.badge && (
          <div className="absolute top-3 right-3">
            <ProviderBadge badge={provider.badge} size="xs" />
          </div>
        )}
        {provider.subscription_tier === 'pro' && provider.waitlist_count >= 2 && (
          <div
            className="absolute bottom-3 right-3 bg-[#1A1A1A]/90 backdrop-blur-sm text-[#D4AF37] text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 shadow-sm"
            data-testid={`waitlist-pill-${provider.provider_id}`}
            title="In demand, clients are on the waitlist"
          >
            <Bell className="h-3 w-3" />
            {provider.waitlist_count} waiting
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="p-4 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-heading text-lg font-semibold text-[#1A1A1A] line-clamp-1 flex-1">{provider.brand_name}</h3>
          {provider.is_tutorial && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200 flex-shrink-0"
              data-testid={`tutorial-card-badge-${provider.provider_id}`}
              title="Tutorial profile, bookings disabled"
            >
              Tutorial
            </span>
          )}
        </div>

        {/* Rating, shows stars if reviews exist, otherwise "New on Zuriya" */}
        {provider.review_count > 0 ? (
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-sm font-semibold text-[#D4AF37]">{provider.rating.toFixed(1)}</span>
            <StarRating rating={provider.rating} />
            <span className="text-xs text-stone-400">{provider.review_count} {provider.review_count === 1 ? 'review' : 'reviews'}</span>
          </div>
        ) : (
          <p className="text-xs text-stone-400 mb-1.5">New on Zuriya</p>
        )}

        {provider.location && (
          <p className="flex items-center gap-1 text-xs text-stone-400 mb-1">
            <MapPin className="h-3 w-3 flex-shrink-0" />
            <span className="line-clamp-1">
              {provider.location}
              {typeof provider.distance_miles === 'number' && (
                <span
                  data-testid={`distance-${provider.provider_id}`}
                  className="text-[#D4AF37] font-medium ml-1"
                >
                  &middot; {provider.distance_miles} mi away
                </span>
              )}
            </span>
          </p>
        )}
      </div>

      {/* Services Preview */}
      {topServices.length > 0 && (
        <div className="px-4 pb-3">
          <div className="border-t border-stone-100 pt-3 space-y-2.5">
            {topServices.map((svc, i) => (
              <ServiceRow key={svc.service_id || `svc-${i}`} svc={svc} />
            ))}
          </div>

          {hasMoreServices && (
            <p className="flex items-center gap-1 text-xs text-[#D4AF37] font-medium mt-3" data-testid={`show-all-${provider.provider_id}`}>
              <ArrowRight className="h-3.5 w-3.5" />
              See all {totalServices} services
            </p>
          )}
        </div>
      )}

      {/* Fallback if no services */}
      {topServices.length === 0 && (
        <div className="px-4 pb-4">
          {provider.starting_price && (
            <p className="text-xs text-stone-400">From &pound;{provider.starting_price}</p>
          )}
          {provider.categories && provider.categories.length > 0 && (
            <div className="flex gap-1 flex-wrap mt-1.5">
              {provider.categories.slice(0, 3).map(cat => (
                <span key={cat} className="text-xs px-2 py-0.5 bg-[#F9F6F0] text-stone-500 rounded capitalize">{cat}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </Link>
  );
}
